// frontend/src/marketing/data/legal.js
//
// Public legal/policy pages. Bodies are markdown rendered by the shared
// Markdown component. IMPORTANT: these are DRAFT TEMPLATES for convenience and
// must be reviewed by legal counsel before launch (a banner says so on-page).
// Product facts used here are real: org-based multi-tenancy, Stripe billing,
// GDPR-first, scans never store a raw IP (anonymised at capture).

export const LEGAL_LAST_UPDATED = "21 June 2026";
const COMPANY = "QRFlow";
const CONTACT = "support@qrflow.ioanna.ro";

export const LEGAL_DOCS = [
  {
    slug: "terms",
    title: "Terms of Service",
    description: "The agreement governing your use of QRFlow.",
    body: `# Terms of Service

These Terms of Service ("Terms") govern your access to and use of ${COMPANY} (the
"Service"). By creating an account or using the Service you agree to these Terms.
If you are using the Service on behalf of an organisation, you agree on its behalf.

## 1. Accounts and organisations
The Service is organised around **organisations** (workspaces). The person who
creates an organisation is its owner and may invite members and assign roles. You
are responsible for the activity of all members of your organisation and for
keeping account credentials secure.

## 2. Acceptable use
Your use of the Service must comply with our [Acceptable Use Policy](/legal/acceptable-use).
You must not use QR codes created with the Service to mislead, defraud, or direct
users to malicious or unlawful content.

## 3. Plans, billing and changes
Paid plans are billed through our payment processor on a monthly or annual basis.
**Upgrades** take effect immediately; **downgrades** take effect at the end of the
current billing period. Each plan allows a set number of **active** QR codes — on a
downgrade or cancellation, any codes above your new tier's limit are automatically
paused until you re-activate them within the limit or upgrade again. Fees are
described on our [Pricing](/pricing) page and may change with notice. Billing and
cancellations are also covered by our [Refund & Billing Policy](/legal/refunds).

## 4. Your content
You retain ownership of the content and destinations you configure. You grant
${COMPANY} the limited rights needed to operate the Service (for example, to store
your codes and serve your redirects). You are responsible for having the rights to
the content you link to. Storage is **best-effort and not indefinite** — you are
responsible for keeping your own backups of anything important, using the export
tools provided. See the [Refund & Billing Policy](/legal/refunds).

## 5. Service availability — no guarantee
We work to keep the Service available, but it is provided strictly on an "as is"
and "as available" basis, **without any uptime, availability, or service-level
guarantee**. The Service may be unavailable, interrupted, delayed, degraded,
modified, or discontinued at any time, with or without notice — including for
maintenance, upgrades, third-party or network failures, or events beyond our
control. ${COMPANY} is not responsible or liable for any downtime, interruption,
delay, data loss, or for QR codes or redirects failing to resolve, and no such
event entitles you to any refund, credit, or compensation.

## 6. Dynamic redirects and your results
Dynamic codes forward to a destination you control and can change at any time.
You are solely responsible for the destinations your codes point to at all times.
${COMPANY} does **not guarantee any result or outcome** from your use of the
Service or your QR codes (including scans, traffic, conversions, or the accuracy
of analytics), and is not responsible for the results of that use.

## 7. AI features and third-party AI providers
The Service includes optional AI features (for example, the in-app assistant and
support agents). **What you type into these features — your messages and any
context they include — is processed by an AI model to generate a response.**
Depending on the configuration in effect, that model may be operated by a
**third-party AI provider** (such as Anthropic, OpenAI, or Google), in which case
the content you submit is **sent to and processed by that provider on their
systems, outside ${COMPANY}'s infrastructure and control**, under their own terms
and privacy practices.

By using the AI features you acknowledge and agree that:
- **You decide what you send.** You are solely responsible for the content you
  submit to the AI features. **Do not submit passwords, payment-card data,
  special-category or sensitive personal data, trade secrets, or any confidential
  information you are not permitted to disclose to a third-party AI provider.**
- **Outputs are not reliable.** AI responses are generated automatically, may be
  inaccurate or incomplete, and are **not advice**. Verify anything you rely on.
  ${COMPANY} does not guarantee any AI output and is **not responsible or liable
  for the results of using it**.
- **Third-party processing is outside our control.** ${COMPANY} is not responsible
  or liable for the acts, omissions, availability, security, or data handling of
  any third-party AI provider, including how it processes, retains, or uses the
  content you submit.

If you do not want your content processed by a third-party AI provider, do not use
the AI features. See our [Data Processing Addendum](/legal/dpa) and
[Subprocessors](/legal/subprocessors) for which providers may be used.

## 8. Suspension and termination
We may suspend or terminate access for breach of these Terms, for unlawful use, or
to protect the Service or its users. You may cancel at any time; see the
[Refund & Billing Policy](/legal/refunds) for what happens to your data.

## 9. Disclaimers, assumption of risk, and limitation of liability
**As-is, no warranties.** To the maximum extent permitted by law, the Service
(including any AI features and their outputs) is provided "as is" and "as
available" with all faults and **without warranties of any kind**, whether
express, implied, or statutory — including any implied warranties of
merchantability, fitness for a particular purpose, title, non-infringement, or
that the Service will be uninterrupted, secure, error-free, or produce any
particular result.

**You assume all risk.** You are solely responsible for your use of the Service,
your QR codes, the destinations they resolve to, and any outcomes of that use.
${COMPANY} is **not responsible or liable for the results of using the Service or
any QR code**, including lost, missed, or misdirected scans, lost revenue or
opportunities, or any decision made in reliance on the Service or its analytics.

**Limitation of liability.** To the maximum extent permitted by law, ${COMPANY}
and its suppliers will not be liable for any indirect, incidental, special,
consequential, exemplary, or punitive damages, nor for any loss of profits,
revenue, data, goodwill, or business interruption, arising out of or relating to
the Service — even if advised of the possibility. Our total aggregate liability
for all claims relating to the Service will not exceed the amount you actually
paid ${COMPANY} for the Service in the three (3) months immediately before the
event giving rise to the claim (or USD 50 if you paid nothing in that period).

**Indemnification.** You agree to indemnify and hold ${COMPANY} harmless from any
claims, damages, liabilities, and expenses (including reasonable legal fees)
arising from your content, your QR codes and the destinations they point to, or
your breach of these Terms or applicable law.

Nothing in these Terms excludes or limits liability that cannot be excluded or
limited under applicable law; where such law applies, the limits above apply only
to the extent permitted.

## 10. Changes to these Terms
We may update these Terms; material changes will be communicated through the
Service or by email. Continued use after changes take effect constitutes
acceptance.

## 11. Contact
Questions about these Terms: ${CONTACT}.`,
  },
  {
    slug: "cookies",
    title: "Cookie Policy",
    description: "What cookies QRFlow uses and how to control them.",
    body: `# Cookie Policy

This policy explains how ${COMPANY} uses cookies and similar technologies. It
should be read alongside our [Privacy Policy](/legal/privacy).

## What cookies we use
QRFlow keeps cookie usage deliberately minimal. We use two categories:

| Category | Purpose | Optional? |
|---|---|---|
| **Essential** | Authentication and security (keeping you signed in, CSRF protection). The Service cannot function without these. | No — always on |
| **Analytics** | Helps us understand and improve how the product is used. | Yes — opt in/out |

We do **not** use advertising or cross-site tracking cookies.

## Managing your choices
On your first visit you are asked to choose **"Accept all"** (essential +
analytics) or **"Essential only"**. You can change your choice at any time using
the **Cookie Preferences** link in the footer. Essential cookies cannot be
disabled because they are required to sign in and use the Service securely.

## Privacy by design
Consistent with our privacy-first approach, QRFlow does not store raw IP addresses
for scan analytics — they are anonymised at the moment a scan is recorded. See the
[Privacy Policy](/legal/privacy) for details.

## Contact
Questions about cookies: ${CONTACT}.`,
  },
  {
    slug: "acceptable-use",
    title: "Acceptable Use Policy",
    description: "What you may and may not do with QRFlow.",
    body: `# Acceptable Use Policy

This Acceptable Use Policy ("AUP") describes prohibited uses of ${COMPANY}. It
forms part of our [Terms of Service](/legal/terms). We may update it as needed to
protect users and the Service.

## Prohibited content and destinations
You must not create codes that link to or distribute:

- Malware, phishing, or other content designed to deceive or harm.
- Illegal content, or content that infringes others' intellectual property.
- Content that harasses, threatens, or incites violence against others.
- Sexual content involving minors, or other content prohibited by law.

## Prohibited conduct
You must not:

- Misrepresent the destination of a code to trick people into scanning it.
- Attempt to gain unauthorised access to the Service, other accounts, or data.
- Interfere with or disrupt the integrity or performance of the Service.
- Use the Service to send unsolicited bulk messages (spam).
- Circumvent plan limits, billing, or usage controls.

## AI features
The AI features may forward what you submit to a third-party AI provider (see
[Terms](/legal/terms) §7). You must not submit to them: passwords, payment-card
data, special-category or otherwise sensitive personal data, others' personal data
you lack the right to share, trade secrets, or any confidential information you are
not permitted to disclose to a third party; nor use them to generate unlawful,
harmful, or abusive content. You are responsible for everything you submit.

## Enforcement
We may investigate suspected violations and may suspend or disable codes, content,
or accounts that violate this AUP, with or without notice depending on severity.

## Reporting abuse
Report suspected abuse to ${CONTACT}. We take reports seriously and act on
verified violations.`,
  },
  {
    slug: "dpa",
    title: "Data Processing Addendum",
    description: "How QRFlow processes personal data on your behalf (GDPR).",
    body: `# Data Processing Addendum

This Data Processing Addendum ("DPA") applies where ${COMPANY} processes personal
data on your behalf as a **processor** under the GDPR and applicable data
protection law. It supplements our [Terms of Service](/legal/terms).

## Roles
For data your organisation controls (e.g. your team members' account details and
the scan analytics for your codes), **you are the controller** and ${COMPANY} is
the **processor**. ${COMPANY} processes that data only on your documented
instructions, which include providing and supporting the Service.

## Nature and purpose of processing
- **Subject matter:** provision of the QRFlow QR-code management and analytics service.
- **Categories of data subjects:** your team members and the end-users who scan your codes.
- **Categories of personal data:** account details (email, name); coarse, anonymised
  scan analytics (device type and approximate location); and, **if you use the AI
  features, any content you choose to submit to them** (your messages and the
  context they include). **Raw IP addresses of scans are not stored** — they are
  anonymised at capture.

## Sub-processors
${COMPANY} uses the sub-processors listed on our
[Subprocessors](/legal/subprocessors) page. We give notice of material changes.

## AI features and submitted content
If you use the AI features, the content you submit (your messages and any context
they include) is processed to generate a response. Depending on the configuration
in effect, this may involve a **third-party cloud AI provider** (e.g. Anthropic,
OpenAI, or Google) acting as a sub-processor, which processes that content **on
its own systems and may be located outside the EEA/UK** — an international transfer
carried out under that provider's safeguards (such as Standard Contractual
Clauses). **You control what you submit** and must not include special-category or
otherwise sensitive personal data in AI prompts. Such content reaches the provider
**because you sent it**, and ${COMPANY} is not responsible for a third-party AI
provider's own processing, retention, or use of it. See
[Terms](/legal/terms) §7 and the [Subprocessors](/legal/subprocessors) list.

## Security
We implement appropriate technical and organisational measures, including
encryption in transit, access controls, and IP anonymisation by design. See our
[Security](/security) page.

## Data subject rights and assistance
We provide tools and reasonable assistance to help you respond to data-subject
requests (access, rectification, erasure). On erasure we anonymise personal data
while retaining aggregate, non-identifying statistics.

## Return and deletion
Retention is **best-effort and not indefinite** — we store as much as we
reasonably can but do not guarantee retention for any set period, and you are
responsible for keeping your own backups (export tools are provided). On
termination, data is handled per the [Refund & Billing Policy](/legal/refunds)
and then deleted or anonymised in line with our retention practices.

## Contact
Data protection enquiries: ${CONTACT}.`,
  },
  {
    slug: "subprocessors",
    title: "Subprocessors",
    description: "Third parties QRFlow uses to provide the Service.",
    body: `# Subprocessors

${COMPANY} uses a small number of third-party sub-processors to provide the
Service. We remain responsible for their compliance under our
[Data Processing Addendum](/legal/dpa). This list is maintained as a template and
should be confirmed against your production deployment before launch.

| Sub-processor | Purpose | Data involved |
|---|---|---|
| Payment processor (Stripe) | Subscription billing and invoicing | Billing contact and payment metadata (card data is handled by the processor, not stored by ${COMPANY}) |
| Cloud hosting / infrastructure provider | Hosting the application and database | All Service data at rest and in transit |
| Error-monitoring provider | Diagnosing errors and reliability | Technical diagnostic data (enabled only when configured) |
| Email delivery provider | Transactional email (invitations, notifications) | Recipient email address and message content |
| AI providers (e.g. Anthropic, OpenAI, Google) | Generating responses for the in-app AI assistant and support agents | The content you submit to the AI features — your messages and any context they include. Used only when an AI feature is invoked **and** the deployment is configured to use a cloud model. |

> **AI processing.** AI features may run on self-hosted models in our own
> infrastructure **or**, depending on the configuration in effect, on a
> **third-party cloud AI provider** (such as Anthropic, OpenAI, or Google). When a
> cloud model is used, the content you submit to the AI features is **sent to and
> processed by that provider on their systems, outside ${COMPANY}'s control**,
> under their own terms and privacy practices. Do not submit sensitive or
> confidential information to the AI features. See [Terms](/legal/terms) §7.

## Changes
We will update this page and, where required, give notice before adding a
sub-processor that processes personal data. Questions: ${CONTACT}.`,
  },
  {
    slug: "refunds",
    title: "Refund & Billing Policy",
    description: "How billing, cancellations, and your data work.",
    body: `# Refund & Billing Policy

This policy explains how billing, plan changes, cancellations, and refunds work.
It supplements our [Terms of Service](/legal/terms) and [Pricing](/pricing).

## Billing cycle
Paid plans are billed in advance on a monthly or annual basis through our payment
processor. Annual plans are billed once per year at a discount compared with
monthly.

## Plan changes
- **Upgrades** take effect immediately and unlock higher limits and features right away.
- **Downgrades** take effect at the end of the current billing period, at which
  point the lower limits apply.

## Cancellations
You can cancel at any time from the billing portal. Cancellation stops future
renewals; you keep access until the end of the period you have already paid for.

## Refunds
All fees are charged in advance and are **non-refundable** — including for partial
billing periods, unused time, downgrades, or periods of non-use — except where a
refund is required by applicable law. Cancelling stops future renewals; it does
not refund amounts already paid. If you believe you were charged in genuine
error, contact ${CONTACT} within 14 days and we will review it in good faith,
but a review is not a guarantee of a refund.

## No service-level guarantee
The Service carries no uptime, availability, or service-level commitment.
Downtime, interruption, delay, maintenance, degraded performance, or
discontinuation of the Service or of any QR code or redirect does **not** entitle
you to a refund, credit, or compensation. See the
[Terms of Service](/legal/terms) for the full disclaimer.

## Plan limits, downgrades, and your codes
Each plan allows a set number of **active** QR codes. If you downgrade or cancel
and have more active codes than your new tier allows, the excess are automatically
**paused** (deactivated) — they stop redirecting until you re-activate them within
your new limit or upgrade again. A downgrade never deletes codes; it only limits
how many stay active.

## Storage, backups, and your data
**Storage is best-effort and not indefinite.** We aim to keep your codes and scan
history while your account is active and to store as much as we reasonably can,
but we do **not** guarantee retention for any particular period and may limit,
age out, or remove data — including after cancellation, prolonged inactivity, or
as needed to operate the Service. **You are responsible for keeping your own
backups.** You can export your data at any time (for example, "Export scans CSV"
on a code, or the export tools in your account). Data removal on account closure
is handled per our [Data Processing Addendum](/legal/dpa).

## Vouchers and referrals
Voucher codes can be redeemed in **Billing → Redeem voucher**, and the referral
programme rewards you when someone you refer subscribes.

## Contact
Billing questions: ${CONTACT}.`,
  },
];

export function getLegalDoc(slug) {
  return LEGAL_DOCS.find((d) => d.slug === slug) || null;
}
